// Event Coordination Service
// Handles all event management, store subscriptions, and inter-component communication

import { mapService } from './mapService.js';
import { riskDataService } from './riskDataService.js';
import { useDataStore } from '../stores/dataStore.js';
import { useFilterStore } from '../stores/filterStore.js';
import { useMapStore } from '../stores/mapStore.js';

export class EventCoordinator {
  constructor() {
    this.listeners = new Map();
    this.storeUnsubscribers = [];
    this.componentManager = null;
    this.filterManager = null;
    this.isInitialized = false;
    this.scoredMineIdsFilter = null;
  }

  initialize(componentManager, filterManager) {
    this.componentManager = componentManager;
    this.filterManager = filterManager;
    this.setupEventHandlers();
    this.isInitialized = true;
    console.log('✅ Event coordinator initialized');
  }

  setupEventHandlers() {
    console.log('🔗 Setting up event handlers...');

    // Map service events
    this.setupMapEvents();

    // Store subscriptions
    this.setupStoreSubscriptions();

    // Global events
    this.setupGlobalEvents();

    // Custom events
    this.setupCustomEvents();

    // Feedback form handlers
    this.setupFeedbackHandlers();

    console.log('✅ Event handlers set up');
  }

  setupMapEvents() {
    // Map service events - use FilterManager for viewport changes
    mapService.on('viewportChanged', (viewport) => {
      const mapStore = useMapStore.getState();
      mapStore.setViewport(viewport);

      // Use FilterManager to sync with viewport and apply filters
      this.filterManager.syncWithViewport(
        viewport.bounds,
        viewport.zoom,
        (data) => {
          // Success callback - update DataStore
          const dataStore = useDataStore.getState();
          // Extract mines array from GeoJSON response if needed
          const minesArray = data.features ? data.features.map(f => f.properties) : (data.mines || data);
          console.log('📊 Viewport FilterManager received', minesArray.length, 'mines, updating filteredMines');
          dataStore.setFilteredMines(minesArray);
          console.log('✅ Viewport updated and filters applied via FilterManager');
        },
        (error) => {
          // Error callback
          console.error('❌ Viewport filter sync failed:', error);
          this.componentManager?.showError('Failed to load mine data for viewport');
        }
      );
    });

    mapService.on('mineClicked', (mine) => {
      const dataStore = useDataStore.getState();
      dataStore.setSelectedMine(mine);
    });
  }

  setupStoreSubscriptions() {
    // Filter store subscription - use FilterManager for proper orchestration
    let lastFilters = JSON.stringify(useFilterStore.getState().getFilters());
    const filterUnsubscriber = useFilterStore.subscribe(async (state) => {
      const currentFilters = JSON.stringify(state.getFilters());
      if (currentFilters !== lastFilters) {
        lastFilters = currentFilters;
        const filters = state.getFilters();
        console.log('🔄 Filter store changed:', filters);

        // Use FilterManager to orchestrate filter application
        await this.filterManager.applyFilters(
          (data) => {
            // Success callback - update DataStore with filtered results
            const dataStore = useDataStore.getState();
            // Extract mines array from GeoJSON response if needed
            const minesArray = data.features ? data.features.map(f => f.properties) : (data.mines || data);
            console.log('📊 FilterManager received', minesArray.length, 'mines, updating filteredMines');
            dataStore.setFilteredMines(minesArray);
            console.log('✅ Filters applied successfully via FilterManager');
          },
          (error) => {
            // Error callback
            console.error('❌ Filter application failed:', error);
            this.componentManager?.showError('Failed to apply filters');
          }
        );
      }
    });

    // Data store subscription for mine updates - watch filteredMines directly
    let lastFilteredMines = [];
    const dataUnsubscriber = useDataStore.subscribe((state) => {
      // Check if filteredMines has changed
      const currentFilteredMines = state.filteredMines;

      console.log('🔄 DataStore state changed, checking filteredMines:', {
        previousLength: lastFilteredMines.length,
        currentLength: currentFilteredMines.length,
        hasChanged: currentFilteredMines !== lastFilteredMines
      });

      // Only update if filteredMines actually changed
      if (currentFilteredMines !== lastFilteredMines) {
        lastFilteredMines = currentFilteredMines;

        console.log('🔄 FilteredMines changed, processing update');

        // Skip if minesData is empty or null
        if (!currentFilteredMines || (Array.isArray(currentFilteredMines) && currentFilteredMines.length === 0)) {
          console.log('🔄 Filtered mines updated but empty, skipping map update');
          return;
        }

        // Extract mine array from GeoJSON if needed
        let minesArray;
        if (currentFilteredMines && currentFilteredMines.type === 'FeatureCollection' && currentFilteredMines.features) {
          minesArray = currentFilteredMines.features.map(feature => feature.properties);
        } else if (Array.isArray(currentFilteredMines)) {
          minesArray = currentFilteredMines;
        } else {
          console.warn('Unexpected mines data format:', currentFilteredMines);
          return;
        }

        console.log('🗺️ Updating map with', minesArray.length, 'filtered mines');

        // DEBUG: Check for specific mines in filtered data
        const pintoInFiltered = minesArray.find(m => m.mine_id === 178832);
        const miamiInFiltered = minesArray.find(m => m.mine_id === 162304);
        console.log('🔍 EventCoordinator - Pinto Valley in filtered mines:', pintoInFiltered ? 'YES' : 'NO');
        console.log('🔍 EventCoordinator - Miami in filtered mines:', miamiInFiltered ? 'YES' : 'NO');
        console.log('🔍 EventCoordinator - scoredMineIdsFilter:', this.scoredMineIdsFilter ? `ACTIVE (${this.scoredMineIdsFilter.length} IDs)` : 'INACTIVE');

        // Enrich mines with risk data
        const enrichedMines = riskDataService.enrichMinesArray(minesArray);

        // Apply scored mines filter if active
        mapService.updateMarkers(enrichedMines, this.scoredMineIdsFilter);

        // Update components with new mine data
        this.componentManager?.updateMapWithMines(enrichedMines);
      }
    });

    // Store unsubscribers for cleanup
    this.storeUnsubscribers.push(filterUnsubscriber, dataUnsubscriber);
  }

  setupGlobalEvents() {
    // Global error handling
    const errorHandler = (error) => {
      console.error('Global error:', error);
      this.componentManager?.showError('An unexpected error occurred');
    };

    // Global keyboard shortcuts
    const keyboardHandler = (e) => {
      this.handleGlobalKeyboard(e);
    };

    window.addEventListener('error', errorHandler);
    document.addEventListener('keydown', keyboardHandler);

    // Store references for cleanup
    this.listeners.set('globalError', { element: window, event: 'error', handler: errorHandler });
    this.listeners.set('globalKeyboard', { element: document, event: 'keydown', handler: keyboardHandler });
  }

  setupCustomEvents() {
    // Reset map view event
    const resetMapHandler = async () => {
      const { mapConfig } = await import('../config/mapConfig.js');
      mapService.flyTo(mapConfig.defaults.center, mapConfig.defaults.zoom);
    };

    // Map zoom events
    const mapZoomHandler = (e) => {
      if (e.detail.direction === 'in') {
        mapService.zoomIn();
      } else if (e.detail.direction === 'out') {
        mapService.zoomOut();
      }
    };

    // Feedback modal event
    const feedbackHandler = (e) => {
      this.handleFeedbackRequest(e.detail);
    };

    document.addEventListener('resetMapView', resetMapHandler);
    document.addEventListener('mapZoom', mapZoomHandler);
    document.addEventListener('openFeedbackModal', feedbackHandler);

    // Store references for cleanup
    this.listeners.set('resetMap', { element: document, event: 'resetMapView', handler: resetMapHandler });
    this.listeners.set('mapZoom', { element: document, event: 'mapZoom', handler: mapZoomHandler });
    this.listeners.set('feedbackModal', { element: document, event: 'openFeedbackModal', handler: feedbackHandler });
  }

  setupFeedbackHandlers() {
    // Open feedback modal button (from mine details sidebar)
    const openFeedbackBtn = document.getElementById('openFeedbackModal');
    if (openFeedbackBtn) {
      const openHandler = () => this.handleFeedbackRequest({});
      openFeedbackBtn.addEventListener('click', openHandler);
      this.listeners.set('openFeedback', { element: openFeedbackBtn, event: 'click', handler: openHandler });
    }

    // Close feedback modal handlers
    const closeFeedbackBtn = document.getElementById('closeFeedbackModal');
    const cancelFeedbackBtn = document.getElementById('cancelFeedback');

    if (closeFeedbackBtn) {
      const closeHandler = () => this.closeFeedbackModal();
      closeFeedbackBtn.addEventListener('click', closeHandler);
      this.listeners.set('closeFeedback', { element: closeFeedbackBtn, event: 'click', handler: closeHandler });
    }

    if (cancelFeedbackBtn) {
      const cancelHandler = () => this.closeFeedbackModal();
      cancelFeedbackBtn.addEventListener('click', cancelHandler);
      this.listeners.set('cancelFeedback', { element: cancelFeedbackBtn, event: 'click', handler: cancelHandler });
    }

    // Form submission
    const feedbackForm = document.querySelector('.feedback-form');
    if (feedbackForm) {
      const submitHandler = (e) => this.handleFeedbackSubmit(e);
      feedbackForm.addEventListener('submit', submitHandler);
      this.listeners.set('submitFeedback', { element: feedbackForm, event: 'submit', handler: submitHandler });
    }

    // Close modal when clicking outside
    const feedbackModal = document.getElementById('feedbackModal');
    if (feedbackModal) {
      const modalClickHandler = (e) => {
        if (e.target === feedbackModal) {
          this.closeFeedbackModal();
        }
      };
      feedbackModal.addEventListener('click', modalClickHandler);
      this.listeners.set('feedbackModalClick', { element: feedbackModal, event: 'click', handler: modalClickHandler });
    }
  }

  handleGlobalKeyboard(e) {
    // Only handle if no input is focused
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      return;
    }

    // ESC to close all panels
    if (e.key === 'Escape') {
      this.componentManager?.hideAllPanels();
    }
  }

  handleFeedbackRequest(detail) {
    // Open feedback modal with context
    const feedbackModal = document.getElementById('feedbackModal');
    if (feedbackModal) {
      feedbackModal.classList.add('visible');

      // Pre-fill context if mine details provided
      if (detail.mine) {
        const messageField = document.getElementById('feedbackMessage');
        if (messageField) {
          messageField.value = `Regarding mine: ${detail.mine.mine_name} (${detail.mine.country})\n\n`;
          messageField.focus();
        }
      }
    }
  }

  closeFeedbackModal() {
    const feedbackModal = document.getElementById('feedbackModal');
    if (feedbackModal) {
      feedbackModal.classList.remove('visible');

      // Clear form
      const form = feedbackModal.querySelector('.feedback-form');
      if (form) {
        form.reset();
      }
    }
  }

  async handleFeedbackSubmit(e) {
    e.preventDefault();

    const email = document.getElementById('feedbackEmail').value;
    const type = document.getElementById('feedbackType').value;
    const message = document.getElementById('feedbackMessage').value;

    if (!email || !message) {
      this.componentManager?.showError('Please fill in all required fields');
      return;
    }

    const submitBtn = document.getElementById('submitFeedback');
    const originalText = submitBtn.textContent;

    try {
      // Show loading state
      submitBtn.textContent = 'Sending...';
      submitBtn.disabled = true;

      // Send email using EmailJS (if configured)
      if (window.emailjs && import.meta.env.VITE_EMAILJS_SERVICE_ID) {
        await window.emailjs.send(
          import.meta.env.VITE_EMAILJS_SERVICE_ID,
          import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
          {
            from_name: email,
            to_name: "Mining Risk Admin",
            from_email: email,
            message: message,
            issue_type: type,
            timestamp: new Date().toISOString()
          },
          import.meta.env.VITE_EMAILJS_PUBLIC_KEY
        );

        this.componentManager?.showSuccess('Feedback sent successfully! Thank you.');
        this.closeFeedbackModal();
      } else {
        throw new Error('Email service not configured');
      }

    } catch (error) {
      console.error('Failed to send feedback:', error);
      this.componentManager?.showError('Failed to send feedback. Please try again.');
    } finally {
      // Restore button state
      submitBtn.textContent = originalText;
      submitBtn.disabled = false;
    }
  }

  addEventListener(key, element, event, handler) {
    // Remove existing listener if it exists
    this.removeEventListener(key);

    // Add new listener
    element.addEventListener(event, handler);
    this.listeners.set(key, { element, event, handler });
  }

  removeEventListener(key) {
    const listener = this.listeners.get(key);
    if (listener) {
      listener.element.removeEventListener(listener.event, listener.handler);
      this.listeners.delete(key);
    }
  }

  destroy() {
    console.log('🧹 Destroying event coordinator...');

    // Remove all event listeners
    this.listeners.forEach((listener, key) => {
      try {
        listener.element.removeEventListener(listener.event, listener.handler);
        console.log(`✅ Event listener '${key}' removed`);
      } catch (error) {
        console.error(`❌ Error removing event listener '${key}':`, error);
      }
    });
    this.listeners.clear();

    // Unsubscribe from stores
    this.storeUnsubscribers.forEach((unsubscriber, index) => {
      try {
        unsubscriber();
        console.log(`✅ Store subscription ${index} removed`);
      } catch (error) {
        console.error(`❌ Error removing store subscription ${index}:`, error);
      }
    });
    this.storeUnsubscribers = [];

    this.componentManager = null;
    this.filterManager = null;
    this.isInitialized = false;

    console.log('✅ Event coordinator destroyed');
  }

  isEventsInitialized() {
    return this.isInitialized;
  }
}

// Export singleton instance
export const eventCoordinator = new EventCoordinator();